import React, { useEffect, useState } from "react";
import tuncayLogo from "../assets/logo/tuncay.png";
import { useNavigate, Link } from "react-router-dom";
import NoAuthFooter from "../components/NoAuthFooter";
import LoginSlider from "../components/LoginSlider";
import AlertModal from "../components/AlertModal";

import {
  IoCardOutline,
  IoFingerPrintOutline,
  IoSchoolOutline,
  IoChevronDownOutline,
  IoChevronUpOutline,
  IoCheckmark,
} from "react-icons/io5";

import { schoolsServices } from "../api/schoolsServices";
import { authServices } from "../api/authServices";

export default function Login() {
  const navigate = useNavigate();
  const [studentNumber, setStudentNumber] = useState("111");
  const [idNumber, setIdNumber] = useState("12345678901");

  const [selectedSchool, setSelectedSchool] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [schoolsArray, setSchoolsArray] = useState([]);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [schoolsError, setSchoolsError] = useState("");

  const [kvkkAccepted, setKvkkAccepted] = useState(false);
  const [serviceAgreementAccepted, setServiceAgreementAccepted] =
    useState(false);

  const [alertConfig, setAlertConfig] = useState({
    visible: false,
    title: "",
    message: "",
    type: "warning",
  });

  const showAlert = (title, message, type = "warning") => {
    setAlertConfig({
      visible: true,
      title,
      message,
      type,
    });
  };

  const hideAlert = () => {
    setAlertConfig((prev) => ({ ...prev, visible: false }));
  };

  useEffect(() => {
    const loadSchools = async () => {
      setSchoolsLoading(true);
      setSchoolsError("");

      try {
        const response = await schoolsServices.getAll();

        if (!response.status) {
          throw new Error("Okullar yüklenemedi.");
        }

        setSchoolsArray(response.data.data || []);
      } catch (error) {
        setSchoolsArray([]);
        setSchoolsError(error.message || "Okullar yüklenemedi.");
      } finally {
        setSchoolsLoading(false);
      }
    };

    loadSchools();
  }, []);

  const handleSelectSchool = (school) => {
    setSelectedSchool(school);
    setIsDropdownOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cleanStudentNumber = (studentNumber || "").trim();
    const cleanIdNumber = (idNumber || "").trim();

    if (!cleanStudentNumber) {
      showAlert("Eksik Bilgi", "Lütfen öğrenci numaranızı giriniz.", "warning");
      return;
    }

    if (!cleanIdNumber) {
      showAlert(
        "Eksik Bilgi",
        "Lütfen T.C. Kimlik numaranızı giriniz.",
        "warning",
      );
      return;
    }

    if (cleanIdNumber.length !== 11) {
      showAlert(
        "Geçersiz TCKN",
        "T.C. Kimlik Numarası 11 haneli olmalıdır.",
        "warning",
      );
      return;
    }

    if (!selectedSchool) {
      showAlert("Eksik Bilgi", "Lütfen listeden bir okul seçiniz.", "warning");
      return;
    }

    if (!kvkkAccepted || !serviceAgreementAccepted) {
      return;
    }

    const formData = new FormData();
    formData.append("school_id", selectedSchool.id);
    formData.append("student_number", cleanStudentNumber);
    formData.append("tc_no", cleanIdNumber);

    try {
      const data = await authServices.login(formData);

      navigate("/sms-verification", {
        replace: true,
        state: {
          school_id: selectedSchool.id,
          student_number: cleanStudentNumber,
          tc_no: cleanIdNumber,
          phoneNumber: data?.phone || data?.data?.phone,
          session_id: data?.session_id || data?.data?.session_id,
        },
      });
    } catch (error) {
      console.log("login error", error);
      showAlert(
        "Giriş Hatası",
        "Giriş yapılırken bir hata oluştu. Lütfen bilgilerinizi kontrol ediniz.",
        "error",
      );
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9ff] flex flex-col antialiased">
      <main className="grow flex items-center justify-center p-6 sm:p-12 md:p-16 max-w-7xl mx-auto w-full">
        <div className="flex flex-col lg:flex-row items-stretch justify-center w-full gap-8">
          <div className="w-full lg:w-[420px] bg-white rounded-3xl shadow-xl border border-gray-100 p-8 sm:p-10 transition-all flex flex-col justify-between shrink-0">
            <div>
              <div className="text-center mb-8">
                <img
                  src={tuncayLogo}
                  alt="Tuncay Seyahat"
                  className="h-16 w-auto mx-auto mb-4"
                />
                <h2 className="text-2xl font-bold text-[#00306b]">
                  Sisteme Giriş
                </h2>
                <p className="text-sm text-gray-500 mt-2">
                  Okul Servisi Ödeme Sistemi
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5" >
                <div>
                  <label className="block text-sm font-semibold text-gray-600 mb-2">
                    Öğrenci Numarası
                  </label>
                  <div className="relative flex items-center">
                    <span className="absolute left-4 text-[#1e3a8a]">
                      <IoCardOutline className="text-xl" />
                    </span>
                    <input
                      type="text"
                      onChange={(e) => setStudentNumber(e.target.value)}
                      placeholder="Öğrenci Numarası"
                      className="w-full bg-gray-50 border border-gray-200 focus:border-blue-600 focus:bg-white focus:ring-0 rounded-xl pl-12 pr-4 py-3.5 text-sm text-[#0b1c30] font-medium transition-all"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-600 mb-2">
                    TC Kimlik Numarası
                  </label>
                  <div className="relative flex items-center">
                    <span className="absolute left-4 text-[#1e3a8a]">
                      <IoFingerPrintOutline className="text-xl" />
                    </span>
                    <input
                      type="text"
                      maxLength={11}
                      onChange={(e) => setIdNumber(e.target.value)}
                      placeholder="TC Kimlik Numarası"
                      className="w-full bg-gray-50 border border-gray-200 focus:border-blue-600 focus:bg-white focus:ring-0 rounded-xl pl-12 pr-4 py-3.5 text-sm text-[#0b1c30] font-medium transition-all"
                      required
                    />
                  </div>
                </div>

                <div className="relative">
                  <label className="block text-sm font-semibold text-gray-600 mb-2">
                    Okul Seçimi
                  </label>

                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className={`w-full bg-gray-50 border border-gray-200 text-left flex items-center justify-between pl-12 pr-4 py-3.5 text-sm font-medium transition-all ${isDropdownOpen
                      ? "rounded-t-xl bg-white border-b-transparent ring-1 ring-blue-600"
                      : "rounded-xl"
                      }`}
                  >
                    <span className="absolute left-4 text-[#1e3a8a]">
                      <IoSchoolOutline className="text-xl" />
                    </span>
                    <span
                      className={
                        selectedSchool ? "text-[#0b1c30]" : "text-gray-400"
                      }
                    >
                      {selectedSchool ? selectedSchool.name : "Okulunuzu seçin"}
                    </span>
                    <span className="text-gray-400">
                      {isDropdownOpen ? (
                        <IoChevronUpOutline className="text-base" />
                      ) : (
                        <IoChevronDownOutline className="text-base" />
                      )}
                    </span>
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute left-0 right-0 bg-white rounded-b-xl max-h-48 overflow-y-auto border border-t-0 border-gray-200 shadow-xl z-30">
                      {schoolsLoading ? (
                        <div className="px-4 py-3 text-sm text-gray-500 flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-[#1e3a8a] border-t-transparent rounded-full animate-spin"></div>
                          Okullar yükleniyor...
                        </div>
                      ) : schoolsError ? (
                        <div className="px-4 py-3 text-sm text-red-500">
                          {schoolsError}
                        </div>
                      ) : schoolsArray.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-500">
                          Gösterilecek okul bulunamadı.
                        </div>
                      ) : (
                        schoolsArray.map((item) => {
                          const isSelected = selectedSchool?.id === item.id;
                          return (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => handleSelectSchool(item)}
                              className={`w-full text-left flex items-center justify-between py-3 px-4 text-sm transition-colors ${isSelected
                                ? "bg-blue-50 text-[#1e3a8a] font-semibold"
                                : "text-gray-700 hover:bg-gray-50"
                                }`}
                            >
                              <span className="truncate">{item.name}</span>
                              {isSelected && (
                                <IoCheckmark className="text-[#1e3a8a] text-base flex-shrink-0" />
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-3 pt-2">
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={kvkkAccepted}
                      onChange={(e) => setKvkkAccepted(e.target.checked)}
                      className="w-4 h-4 mt-0.5 text-[#1e3a8a] border-gray-300 rounded focus:ring-blue-500 accent-[#1e3a8a]"
                      required
                    />
                    <span className="text-xs sm:text-sm text-gray-600 font-medium select-none group-hover:text-gray-900 transition-colors">
                      <Link
                        to="/kvkk"
                        onClick={(e) => e.stopPropagation()}
                        className="text-[#1e3a8a] underline hover:text-blue-800 font-semibold mr-1"
                      >
                        KVKK sözleşmesini
                      </Link>
                      okudum ve kabul ediyorum.
                    </span>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={serviceAgreementAccepted}
                      onChange={(e) =>
                        setServiceAgreementAccepted(e.target.checked)
                      }
                      className="w-4 h-4 mt-0.5 text-[#1e3a8a] border-gray-300 rounded focus:ring-blue-500 accent-[#1e3a8a]"
                      required
                    />
                    <span className="text-xs sm:text-sm text-gray-600 font-medium select-none group-hover:text-gray-900 transition-colors">
                      <Link
                        to="/service-agreement"
                        onClick={(e) => e.stopPropagation()}
                        className="text-[#1e3a8a] underline hover:text-blue-800 font-semibold mr-1"
                      >
                        Hizmet Sözleşmesini
                      </Link>
                      okudum ve kabul ediyorum.
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  className="w-full bg-[#1e3a8a] hover:bg-blue-800 text-white font-semibold py-4 rounded-xl transition-colors shadow-md flex items-center justify-center gap-2 mt-4 text-sm"
                >
                  Giriş Yap
                </button>
              </form>
            </div>
          </div>

          <LoginSlider />
        </div>
      </main>
      <NoAuthFooter />

      <AlertModal
        visible={alertConfig.visible}
        onClose={hideAlert}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
      />
    </div>
  );
}
